import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { ContentContainer } from '~/eds/src/components/ContentContainer/ContentContainer';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { useConversationPipeline } from '~/features/ask-anything/hooks/useConversationPipeline';
import { api } from '~/redux/api/createApi';
import { Question } from '~/types/QuestionGroup';

import { useQuestionGroupFormModal } from '../QuestionGroups/useQuestionGroupFormModal';
import { QuestionList } from './QuestionList';
import { SearchAndFilters } from './SearchAndFilters';

export const Questions = ({ disableRun }: { disableRun?: boolean }) => {
  const intl = useIntl();
  const [debouncedSearch, search, setSearch] = useDebouncedState<string>('');
  const [questionGroupFormModal, openQuestionGroupFormModal] =
    useQuestionGroupFormModal({
      questionGroup: null,
    });
  const {
    data: questions,
    isError,
    isFetching,
  } = api.endpoints.getQuestions.useQuery({
    search: debouncedSearch,
  });

  const { toast } = useToast();

  const onFinishQueue = () => {
    setTimeout(() => {
      clearQueue();
    }, 3000);
  };

  const {
    postMessage,
    clearQueue,
    processingMessage: runningQuestion,
    queue: messageQueue,
  } = useConversationPipeline({
    onFinishQueue,
  });

  const runQuestion = (question: Question) => {
    postMessage({ id: question.id, message: question.text });
  };

  // Clear the queue when the component unmounts
  useEffect(() => {
    return clearQueue;
  }, []);

  useEffect(() => {
    if (isError) {
      toast(failedToLoadQuestionsMessage);
    }
  }, [isError]);

  const renderStatusMessage = () => {
    if (isError) {
      return <StatusMessage {...failedToLoadQuestionsMessage} />;
    }

    if (!isFetching && debouncedSearch && questions?.length === 0) {
      return (
        <StatusMessage
          status="info"
          title={intl.formatMessage({
            id: 'Questions.noSearchResultsTitle',
            defaultMessage: 'No saved questions match the search',
            description: 'Title when no questions match search criteria',
          })}
          message={intl.formatMessage({
            id: 'Questions.noSearchResultsMessage',
            defaultMessage: 'Try searching with a different text.',
            description: 'Message when no questions match search criteria',
          })}
        />
      );
    }

    if (!isFetching && !debouncedSearch && questions?.length === 0) {
      return (
        <StatusMessage
          status="info"
          title={intl.formatMessage({
            id: 'Questions.noQuestionsTitle',
            defaultMessage: 'No saved questions yet',
            description: 'Title when no questions exist',
          })}
          message={intl.formatMessage({
            id: 'Questions.noQuestionsMessage',
            defaultMessage:
              'Save a question by clicking on the bookmark icon on the right of a question.',
            description: 'Message when no questions exist',
          })}
        />
      );
    }

    return null;
  };

  const questionWithStatus = useMemo(
    () =>
      (questions ?? [])?.map((question) => {
        const status = messageQueue.find(
          (message) => message.id === question.id,
        )?.status;
        return {
          ...question,
          status: status ?? ('idle' as const),
        };
      }),
    [questions, messageQueue],
  );

  const failedToLoadQuestionsMessage = {
    status: 'warning' as const,
    title: intl.formatMessage({
      id: 'Questions.errorTitle',
      defaultMessage: 'Error: Unable to Load Saved Questions',
      description: 'Error title when questions fail to load',
    }),
    message: intl.formatMessage({
      id: 'Questions.errorMessage',
      defaultMessage: 'Failed to load saved questions.',
      description: 'Error message when questions fail to load',
    }),
  };

  return (
    <Layout direction="column" styles={componentStyles.mainContainer}>
      <Layout
        spacing={4}
        direction="column"
        styles={componentStyles.questionsContainer}
      >
        <SearchAndFilters search={search} onSearch={setSearch} />
        {renderStatusMessage() ?? (
          <ContentContainer
            loadingContent={{
              isLoading: isFetching,
              message: intl.formatMessage({
                id: 'Questions.loadingMessage',
                defaultMessage: 'Loading questions...',
                description: 'Loading message for questions',
              }),
            }}
          >
            <QuestionList
              questions={questionWithStatus}
              disableRun={!!runningQuestion?.id || !!disableRun}
              onQuestionClick={runQuestion}
            />
          </ContentContainer>
        )}
      </Layout>
      {!isFetching && questions && questions.length > 0 && (
        <Layout
          justify="center"
          align="center"
          styles={componentStyles.createQuestionGroupButton}
        >
          <Button
            icon="plus"
            iconPosition="left"
            variant="action"
            text={intl.formatMessage({
              id: 'Questions.createQuestionGroupButton',
              defaultMessage: 'Create a Question Group',
              description: 'Button text to create a question group',
            })}
            onClick={openQuestionGroupFormModal}
          />
        </Layout>
      )}
      {questionGroupFormModal}
    </Layout>
  );
};

const componentStyles = {
  mainContainer: {
    overflow: 'hidden',
    height: '100%',
    justifyContent: 'space-between',
    width: '100%',
    position: 'relative',
    flex: 1,
  },
  questionsContainer: {
    overflow: 'hidden',
    flex: 1,
    marginBottom: 8,
  },
  createQuestionGroupButton: {
    bottom: 0,
    width: '100%',
    position: 'absolute',
  },
};
